import type {
  Batch,
  BatchConfig,
  BatchSummary,
  FeedBatch,
  GalleryImage,
  ImageItem,
  Meta,
  PlanResult,
  Preset,
  TagCount,
  UploadItem,
  UrlPreview,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "same-origin",
    headers: init?.body instanceof FormData ? undefined : { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `通信エラー (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* JSON でないレスポンス */
    }
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  session: () => request<{ authed: boolean }>("/auth/session"),
  login: (password: string) =>
    request<{ authed: boolean }>("/auth/login", { method: "POST", body: JSON.stringify({ password }) }),
  logout: () => request<{ authed: boolean }>("/auth/logout", { method: "POST" }),

  meta: () => request<Meta>("/meta"),

  plan: (config: BatchConfig, signal?: AbortSignal) =>
    request<PlanResult>("/plan", { method: "POST", body: JSON.stringify(config), signal }),

  createBatch: (config: BatchConfig, ignoreFailedUrls = false) =>
    request<{ batch: Batch; images: ImageItem[] }>("/batches", {
      method: "POST",
      body: JSON.stringify({ ...config, ignoreFailedUrls }),
    }),

  feed: (opts: { limit?: number; before?: string } = {}) => {
    const params = new URLSearchParams({ limit: String(opts.limit ?? 8) });
    if (opts.before) params.set("before", opts.before);
    return request<{ batches: FeedBatch[]; hasMore: boolean }>(`/feed?${params.toString()}`);
  },
  batches: (limit = 30) => request<{ batches: BatchSummary[] }>(`/batches?limit=${limit}`),
  extendBatch: (id: string, variantsPer: number) =>
    request<{ added: number; images: ImageItem[] }>(`/batches/${id}/extend`, {
      method: "POST",
      body: JSON.stringify({ variantsPer }),
    }),
  batch: (id: string) =>
    request<{ batch: Batch; images: ImageItem[]; running: boolean }>(`/batches/${id}`),
  cancelBatch: (id: string) => request<{ canceled: boolean }>(`/batches/${id}/cancel`, { method: "POST" }),
  retryFailed: (id: string) => request<{ retried: number }>(`/batches/${id}/retry-failed`, { method: "POST" }),
  deleteBatch: (id: string) => request<{ deleted: boolean }>(`/batches/${id}`, { method: "DELETE" }),

  updateImage: (id: string, patch: { favorite?: boolean; tags?: string[] }) =>
    request<{ image: ImageItem }>(`/images/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  setFavorite: (id: string, favorite: boolean) =>
    request<{ image: ImageItem }>(`/images/${id}`, { method: "PATCH", body: JSON.stringify({ favorite }) }),

  gallery: (opts: { favorite?: boolean; tags?: string[]; offset?: number; limit?: number }) => {
    const params = new URLSearchParams({ limit: String(opts.limit ?? 60) });
    if (opts.favorite) params.set("favorite", "1");
    if (opts.tags?.length) params.set("tags", opts.tags.join(","));
    if (opts.offset) params.set("offset", String(opts.offset));
    return request<{ images: GalleryImage[]; total: number; hasMore: boolean }>(
      `/gallery?${params.toString()}`,
    );
  },
  tags: () => request<{ tags: TagCount[] }>("/tags"),

  urlPreview: (text: string, signal?: AbortSignal) =>
    request<{ sources: UrlPreview[]; uploads: UploadItem[] }>("/url-preview", {
      method: "POST",
      body: JSON.stringify({ text }),
      signal,
    }),
  deleteImage: (id: string) => request<{ deleted: boolean }>(`/images/${id}`, { method: "DELETE" }),
  useAsReference: (id: string) =>
    request<{ upload: UploadItem }>(`/images/${id}/use-as-reference`, { method: "POST" }),

  upload: async (files: File[]): Promise<UploadItem[]> => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    const res = await request<{ uploads: UploadItem[] }>("/uploads", { method: "POST", body: fd });
    return res.uploads;
  },

  presets: () => request<{ presets: Preset[] }>("/presets"),
  createPreset: (name: string, note: string, config: BatchConfig) =>
    request<{ preset: Preset }>("/presets", { method: "POST", body: JSON.stringify({ name, note, config }) }),
  updatePreset: (id: string, patch: Partial<{ name: string; note: string; pinned: boolean; touch: boolean }>) =>
    request<{ preset: Preset }>(`/presets/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deletePreset: (id: string) => request<{ deleted: boolean }>(`/presets/${id}`, { method: "DELETE" }),
};

export const zipUrl = (batchId: string, favoriteOnly = false) =>
  `/api/batches/${batchId}/zip${favoriteOnly ? "?favorite=1" : ""}`;
export const downloadUrl = (imageId: string) => `/api/images/${imageId}/download`;
export const fileUrl = (fileName: string) => `/files/images/${fileName}`;

/**
 * 一覧用の縮小画像。原寸は1枚1.5MB前後あるので、並べて見るときは必ずこちらを使う。
 * 幅は 96 / 192 / 400 / 800 のみ（サーバー側で丸められる）。
 */
export const thumbUrl = (fileName: string, width: 96 | 192 | 400 | 800 = 400) =>
  `/files/thumb/images/${fileName}?w=${width}`;

/** アップロード済み画像（参照画像・ページ画像）の縮小版 */
export const uploadThumbUrl = (url: string, width: 96 | 192 | 400 | 800 = 192) => {
  const name = url.split("/").pop() ?? "";
  return `/files/thumb/uploads/${name}?w=${width}`;
};
